import React, {useState} from "react";
import {Box, Dialog, DialogContent, IconButton, Stack, Typography, useTheme} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface PhotoGalleryProps {
    photos: string[]
    tripName: string
}

export const TripPhotoGallery: React.FC<PhotoGalleryProps> = ({photos, tripName}) => {
    const theme = useTheme()
    const primaryGreen = theme.palette.primary.main

    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)

    const openLightbox = (index: number) => {
        setActiveIndex(index)
        setLightboxOpen(true)
    }

    const prev = () => setActiveIndex((i) => (i - 1 + photos.length) % photos.length)
    const next = () => setActiveIndex((i) => (i + 1) % photos.length)

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') prev()
        if (e.key === 'ArrowRight') next()
        if (e.key === 'Escape') setLightboxOpen(false)
    }

    return (
        <>
            {/* Mosaic grid — first image is hero spanning 2 rows */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr 1fr', md: '2fr 1fr 1fr'},
                    gridTemplateRows: {xs: '130px 130px', md: '170px 170px'},
                    gap: 1,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component="img"
                    src={photos[0]}
                    alt={`${tripName} 1`}
                    onClick={() => openLightbox(0)}
                    sx={{
                        gridRow: '1 / 3',
                        width: '100%', height: '100%',
                        objectFit: 'cover', cursor: 'pointer',
                        transition: 'opacity 0.25s, transform 0.35s',
                        '&:hover': {opacity: 0.88, transform: 'scale(1.02)'},
                    }}
                />
                {photos.slice(1).map((src, i) => (
                    <Box
                        key={i}
                        component="img"
                        src={src}
                        alt={`${tripName} ${i + 2}`}
                        onClick={() => openLightbox(i + 1)}
                        sx={{
                            width: '100%', height: '100%',
                            objectFit: 'cover', cursor: 'pointer',
                            transition: 'opacity 0.25s, transform 0.35s',
                            '&:hover': {opacity: 0.88, transform: 'scale(1.02)'},
                        }}
                    />
                ))}
            </Box>

            {/* Lightbox */}
            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                maxWidth="lg"
                fullWidth
                onKeyDown={handleKeyDown}
                PaperProps={{sx: {bgcolor: '#000', boxShadow: 'none', borderRadius: 3}}}
            >
                <DialogContent
                    sx={{
                        p: 0, position: 'relative',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        minHeight: 420,
                    }}
                >
                    <IconButton
                        onClick={() => setLightboxOpen(false)}
                        sx={{position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 10}}
                    >
                        <CloseIcon/>
                    </IconButton>

                    <IconButton onClick={prev} sx={{
                        position: 'absolute', left: 8, color: '#fff', zIndex: 10,
                        bgcolor: 'rgba(0,0,0,0.4)', '&:hover': {bgcolor: 'rgba(0,0,0,0.7)'},
                    }}>
                        <ArrowBackIosNewIcon/>
                    </IconButton>

                    <Box
                        component="img"
                        src={photos[activeIndex]}
                        alt={`${tripName} ${activeIndex + 1}`}
                        sx={{maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain'}}
                    />

                    <IconButton onClick={next} sx={{
                        position: 'absolute', right: 8, color: '#fff', zIndex: 10,
                        bgcolor: 'rgba(0,0,0,0.4)', '&:hover': {bgcolor: 'rgba(0,0,0,0.7)'},
                    }}>
                        <ArrowForwardIosIcon/>
                    </IconButton>

                    {/* Dot indicators */}
                    <Stack direction="row" spacing={0.5} sx={{
                        position: 'absolute', bottom: 12,
                        left: '50%', transform: 'translateX(-50%)',
                    }}>
                        {photos.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => setActiveIndex(i)}
                                sx={{
                                    width: i === activeIndex ? 20 : 8, height: 8,
                                    borderRadius: 4,
                                    bgcolor: i === activeIndex ? primaryGreen : 'rgba(255,255,255,0.4)',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                }}
                            />
                        ))}
                    </Stack>

                    <Typography sx={{
                        position: 'absolute', bottom: 14, right: 16,
                        color: 'rgba(255,255,255,0.45)',
                        fontFamily: '"DM Mono", monospace', fontSize: '0.7rem',
                    }}>
                        {activeIndex + 1} / {photos.length}
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    )
}